import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const ONE_ON_ONE_PERIODS = ['day_1_30', 'day_31_60', 'day_61_90'];
export const ONE_ON_ONE_STATUSES = ['scheduled', 'conducted', 'cancelled', 'no_show'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useObOneOnOnesApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/onboarding-one-on-ones?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const conduct = useCallback(async (id, body) => {
    try { await api.post(`/v1/onboarding-one-on-ones/${id}/conduct`, body || {}); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const cancel = useCallback(async (id, reason) => {
    try { await api.post(`/v1/onboarding-one-on-ones/${id}/cancel`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const markNoShow = useCallback(async (id) => {
    try { await api.post(`/v1/onboarding-one-on-ones/${id}/no-show`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const reschedule = useCallback(async (id, scheduledAt) => {
    try { await api.post(`/v1/onboarding-one-on-ones/${id}/reschedule`, { scheduledAt }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const toPayload = useCallback((formData) => ({
    onboardingId: formData.onboardingId || null,
    period: formData.period || 'day_1_30',
    scheduledAt: formData.scheduledAt,
    agendaTemplateKey: formData.agendaTemplateKey || null,
  }), []);

  const create = useCallback(async (formData) => {
    try { await api.post('/v1/onboarding-one-on-ones', toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const columns = useMemo(() => {
    const periodLabel = (s) => t(`onboardingOneOnOne.period.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`onboardingOneOnOne.status.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('onboardingOneOnOne.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'period',
        label: t('onboardingOneOnOne.periodLabel'),
        options: {
          ...multiSelectFilter(t('onboardingOneOnOne.periodLabel'), ONE_ON_ONE_PERIODS, periodLabel),
          customBodyRender: (v) => periodLabel(v),
        },
      },
      {
        name: 'status',
        label: t('onboardingOneOnOne.statusLabel'),
        options: {
          ...multiSelectFilter(t('onboardingOneOnOne.statusLabel'), ONE_ON_ONE_STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      dateFilterColumn('scheduledAt', t('onboardingOneOnOne.scheduledAt'), lang, { includeTime: true }),
      {
        name: 'conductedAt',
        label: t('onboardingOneOnOne.conductedAt'),
        options: { filter: false, sort: true, customBodyRender: (v) => v ? new Date(v).toLocaleString() : '-' },
      },
    ];
  }, [t, lang, data]);

  const objectSchema = useMemo(() => ([
    {
      name: 'onboardingId', label: t('onboardingOneOnOne.form.onboarding'), type: 'asyncSelect', required: true,
      onSearch: async (q) => {
        const params = q ? { 'personName[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/onboardings', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((o) => ({ id: o.id, name: o.personFullName }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/onboardings/${id}`);
        const o = res.data?.data || {};
        return { id, name: o.personFullName || '' };
      },
    },
    {
      name: 'period', label: t('onboardingOneOnOne.periodLabel'), type: 'select', required: true, grid: 6,
      options: ONE_ON_ONE_PERIODS.map((p) => ({ id: p, name: t(`onboardingOneOnOne.period.${p}`, { defaultValue: p }) })),
    },
    { name: 'scheduledAt', label: t('onboardingOneOnOne.scheduledAt'), type: 'datetime', required: true, grid: 6 },
    {
      name: 'agendaTemplateKey', label: t('onboardingOneOnOne.form.agendaTemplateKey'), type: 'text',
      description: t('onboardingOneOnOne.form.agendaTemplateKeyHint'),
    },
  ]), [t]);

  return { fetching, data, pagination, columns, objectSchema, refresh, create, conduct, cancel, markNoShow, reschedule };
};

export default useObOneOnOnesApi;
